/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import Button, { Icons } from "../buttons/Button";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  transformDFSIConnectionResponseRow,
} from "../../util";

import AuthContext from "../../store/authContext";
import InlineLoader from "../loader/InlineLoader";
import EditDFSIChannelRowFormFields from "./EditDFSIChannelRowFormFields";
import { EDIT_DFSI_CHANNEL_FORM_FIELDS } from "../../constants";
import {
  EDIT_DFSI_CHANNEL_TRANSLATIONS,
  EDIT_ROW_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  setShowEditPanel: any;
  org: string;
  panelName: string;
  esRef: string;
  initialState: any;
  formFields: any;
  header: string;
  transformResponseRow: any;
  transformUpdateBody?: any;
  onUpdate?: any;
  loadDataByRef: any;
  deleteModalItemName?: any;
  onDelete?: any;
  hasDeletePermission?: boolean;
  hasEditPermission?: boolean;
  refreshTable?: any;
}

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;
  const isConnectionFormInvalid = EDIT_DFSI_CHANNEL_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data[field.name]
            );
          case "HEX":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data[field.name]
            );
          case "DROPDOWN":
            return field.isInputInvalid?.(data[field.name]);
          case "COMBO-DROPDOWN":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data[field.name]
            );
          case "CUSTOM-DROPDOWN":
            return data[field.name] && data[field.name].length <= 0;
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const EditDFSIChannelRow = ({
  setShowEditPanel,
  onDelete,
  esRef,
  org,
  panelName,
  initialState,
  formFields,
  header,
  transformResponseRow,
  transformUpdateBody,
  onUpdate,
  loadDataByRef,
  deleteModalItemName,
  hasDeletePermission = false,
  hasEditPermission = false,
  refreshTable,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const { token } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [data, setData] = useState(initialState);
  const [initialData, setInitialData] = useState(data);
  const [loading, setLoading] = useState(false);
  const [dfsiVersion, setDfsiVersion] = useState(undefined);
  const [isChecked, setIsChecked] = useState(false);

  /* 
  Retrieves DFSI version information asynchronously for a specified connection ID.
  Uses API to fetch DFSI connection details and transforms the response to extract DFSI version.
  Sets the DFSI version state and handles errors with an error alert.

  @param connectionID The unique identifier for the DFSI connection.
  @returns The DFSI version number retrieved from the API response, or undefined if an error occurs.
  */

  const handleFetchDFSIVersion = async (connectionID: string) => {
    let response;
    try {
      // Construct query parameters for API request
      const queryParams = new URLSearchParams({
        page: "1",
        pageSize: "20",
        ...(connectionID && {
          "CONNECTION-ID": connectionID,
        }),
      });

      // Make API request to fetch DFSI connection data
      response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/dfsi-connections?${queryParams}`,
        token,
        org,
      });

      // Transform the response to extract DFSI version data
      const dfsiConnectionData = transformDFSIConnectionResponseRow(
        response.Rows[0]
      );
      setDfsiVersion(dfsiConnectionData["DFSI-VERSION"]); // Set DFSI version state

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e); // Handle and alert on any caught errors
    }

    // Return DFSI version from the API response if available, otherwise undefined
    return response && response.Rows[0]["DFSI-VERSION"];
  };

  /*
   Handles default check based on DFSI version and response data.Sets the state of `isChecked` based on specified conditions.

  @param dfsiVersion - The DFSI version number to compare against.
  @param response - The response object containing channel mode and squelch system data.
  */
  const handleDefaultCheck = (dfsiVersion: number, response: any) => {
    let result = false;
    // Check if channel mode is "Analog" and DFSI version is 2
    if (response["CHANNEL-MODE"] === "Analog" && dfsiVersion === 2) {
      if (
        // Check if both CTC and CDC squelch systems are "None" or null
        (response["CTC-SQUELCH-SYSTEM"] === "None" &&
          response["CDC-SQUELCH-SYSTEM"] === "None") ||
        (response["CTC-SQUELCH-SYSTEM"] === null &&
          response["CDC-SQUELCH-SYSTEM"] === null)
      ) {
        result = true; // Set result to true if conditions are met
      }
    }
    setIsChecked(result); // Set the state of isChecked based on result
  };

  const getDataByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await loadDataByRef({
          token,
          org,
          ref,
        });
        setData(transformResponseRow(response));
        setInitialData(transformResponseRow(response));
        const dfsiVersion = await handleFetchDFSIVersion(
          response["CONNECTION-ID"]
        );
        handleDefaultCheck(dfsiVersion, response);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const updateData = async () => {
    setLoading(true);
    try {
      await onUpdate({
        body: transformUpdateBody(dfsiVersion, data, isChecked),
        token,
        ref: esRef,
        org,
      });
      if (refreshTable) {
        refreshTable();
      }
      getDataByRef(esRef);
      successAlert(EDIT_DFSI_CHANNEL_TRANSLATIONS.successAlertMessage);
      setIsEditing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDataByRef(esRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esRef]);

  return (
    <>
      <aside
        id="sidepanel"
        className="sidepanel sidepanel-edit fixed z-40 h-full transform transition-transform ease-in duration-150 bg-white"
      >
        <div className="panel-header">
          <div className="panel-left-side">
            <Button
              kind="ghost"
              className="close-btn"
              icon={Icons.Close}
              onClick={() => setShowEditPanel()}
              dataCy="closeEditDFSIChannelSidePanel-"
            />
            <h5> {panelName} </h5>
          </div>

          {!isEditing ? (
            <div className="panel-right-side tab-header-buttons">
              {onUpdate ? (
                <Button
                  className="w-sm mr-2"
                  kind="tertiary"
                  icon={Icons.Edit}
                  disabled={!hasEditPermission}
                  label={EDIT_DFSI_CHANNEL_TRANSLATIONS.btnLabel.edit}
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  dataCy="editDFSIChannel-"
                />
              ) : null}

              {onDelete ? (
                <Button
                  kind="tertiary"
                  className="w-sm"
                  label={EDIT_DFSI_CHANNEL_TRANSLATIONS.btnLabel.delete}
                  disabled={!hasDeletePermission}
                  icon={Icons.Delete}
                  onClick={() => {
                    return setOpen(true);
                  }}
                  dataCy="deleteDFSIChannel-"
                />
              ) : null}
            </div>
          ) : (
            <div className="tab-header-buttons">
              <Button
                className="w-sm mr-2"
                type="button"
                kind="tertiary"
                disabled={false}
                label={EDIT_DFSI_CHANNEL_TRANSLATIONS.btnLabel.cancel}
                onClick={() => {
                  setOpenEdit(true);
                }}
                dataCy="cancelEditDFSIChannel-"
              />
              <Button
                className="w-sm"
                type="button"
                loading={loading}
                disabled={isFormInvalid({ data })}
                onClick={updateData}
                label={EDIT_DFSI_CHANNEL_TRANSLATIONS.btnLabel.save}
                dataCy="saveDFSIChannel-"
              />
            </div>
          )}
        </div>

        <div>
          <div className="data-tabs">
            {dataLoading ? (
              <InlineLoader
                description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
              />
            ) : (
              <EditDFSIChannelRowFormFields
                data={data}
                isEditing={isEditing}
                esRef={esRef}
                setData={setData}
                formFields={formFields}
                dfsiVersion={dfsiVersion}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
              />
            )}
          </div>
        </div>
      </aside>
      <DeleteModal
        modalHeaderText={EDIT_DFSI_CHANNEL_TRANSLATIONS.cancelModal.header(
          header
        )}
        modalDescriptionText={
          EDIT_DFSI_CHANNEL_TRANSLATIONS.cancelModal.description
        }
        onDelete={() => {
          setIsEditing(false);
          setData(initialData);
          setOpenEdit(false);
        }}
        open={openEdit}
        setOpen={setOpenEdit}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_DFSI_CHANNEL_TRANSLATIONS.deleteModal.header(
          header
        )}
        modalDescriptionText={EDIT_DFSI_CHANNEL_TRANSLATIONS.deleteModal.description(
          header,
          data?.[deleteModalItemName]
        )}
        onDelete={() => onDelete(esRef)}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};

export default EditDFSIChannelRow;
